import React, { useEffect, useState } from 'react';
import { PlusIcon, EditIcon, TrashIcon, UserIcon, MailIcon, KeyIcon, ShieldCheckIcon } from 'lucide-react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Select } from '../ui/Select';
import { Modal } from '../ui/Modal';
import { Table } from '../ui/Table';
import { Badge } from '../ui/Badge';
import { User, UserRole } from '../../types';
import { supabase } from '../../utils/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { useData } from '../../contexts/DataContext';
import { getRoleName, generateId } from '../../utils/helpers';
export function UserManagement() {
  const {
    isConnectedToSupabase
  } = useAuth();
  const {
    roles
  } = useData();
  const [users, setUsers] = useState<User[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'administrador' as UserRole
  });
  const [saveMessage, setSaveMessage] = useState('');
  const [isLoadingUsers, setIsLoadingUsers] = useState(true);
  // Build role options: "administrador" base + dynamic roles from DataContext
  const roleOptions = [{
    value: 'administrador',
    label: 'Administrador'
  }, ...roles.map(r => ({
    value: r.name,
    label: r.displayName || r.name
  }))];
  // Load users from Supabase
  const loadUsers = async () => {
    setIsLoadingUsers(true);
    if (isConnectedToSupabase) {
      try {
        const {
          data,
          error
        } = await supabase.from('users').select('*').order('created_at', {
          ascending: false
        });
        if (data && !error) {
          setUsers(data.map(u => ({
            id: u.id,
            email: u.email,
            password: u.password || '',
            name: u.name,
            role: u.role as UserRole,
            avatar: u.avatar,
            language: u.language || 'es',
            timezone: u.timezone || 'America/Santo_Domingo',
            createdAt: new Date(u.created_at)
          })));
          console.log('✅ Usuarios cargados desde Supabase:', data.length);
        }
      } catch (e) {
        console.error('Error cargando usuarios:', e);
      }
    }
    setIsLoadingUsers(false);
  };
  useEffect(() => {
    loadUsers();
  }, [isConnectedToSupabase]);
  const handleOpenModal = (user?: User) => {
    if (user) {
      setEditingUser(user);
      setFormData({
        name: user.name,
        email: user.email,
        password: '',
        role: user.role
      });
    } else {
      setEditingUser(null);
      setFormData({
        name: '',
        email: '',
        password: '',
        role: 'administrador'
      });
    }
    setIsModalOpen(true);
  };
  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingUser(null);
    setFormData({
      name: '',
      email: '',
      password: '',
      role: 'administrador'
    });
  };
  const handleSaveUser = async () => {
    if (!formData.name || !formData.email) {
      alert('Por favor completa todos los campos requeridos');
      return;
    }
    if (!editingUser && !formData.password) {
      alert('La contraseña es requerida para nuevos usuarios');
      return;
    }
    if (isConnectedToSupabase) {
      try {
        if (editingUser) {
          // Update in Supabase
          const updateData: any = {
            name: formData.name,
            email: formData.email.toLowerCase().trim(),
            role: formData.role
          };
          if (formData.password) {
            updateData.password = formData.password;
          }
          const {
            error
          } = await supabase.from('users').update(updateData).eq('id', editingUser.id);
          if (error) {
            alert('Error actualizando usuario: ' + error.message);
            return;
          }
          setSaveMessage('✅ Usuario actualizado en Supabase');
        } else {
          // Create in Supabase - generate UUID since table may not have default
          const {
            error
          } = await supabase.from('users').insert({
            id: crypto.randomUUID(),
            email: formData.email.toLowerCase().trim(),
            password: formData.password,
            name: formData.name,
            role: formData.role,
            language: 'es',
            timezone: 'America/Santo_Domingo'
          });
          if (error) {
            alert('Error creando usuario: ' + error.message);
            return;
          }
          setSaveMessage('✅ Usuario creado en Supabase');
        }
        // Reload users from Supabase
        await loadUsers();
      } catch (e: any) {
        alert('Error: ' + (e.message || 'Error desconocido'));
        return;
      }
    } else {
      // Fallback localStorage
      if (editingUser) {
        setUsers(prev => prev.map(u => u.id === editingUser.id ? {
          ...u,
          name: formData.name,
          email: formData.email,
          role: formData.role,
          ...(formData.password && {
            password: formData.password
          })
        } : u));
        setSaveMessage('Usuario actualizado');
      } else {
        const newUser: User = {
          id: generateId(),
          name: formData.name,
          email: formData.email,
          password: formData.password,
          role: formData.role,
          language: 'es',
          timezone: 'America/Santo_Domingo',
          createdAt: new Date()
        };
        setUsers(prev => [...prev, newUser]);
        setSaveMessage('Usuario creado');
      }
    }
    handleCloseModal();
    setTimeout(() => setSaveMessage(''), 3000);
  };
  const handleDeleteUser = async (userId: string) => {
    if (!confirm('¿Estás seguro de que deseas eliminar este usuario?')) return;
    if (isConnectedToSupabase) {
      const {
        error
      } = await supabase.from('users').delete().eq('id', userId);
      if (error) {
        alert('Error eliminando usuario: ' + error.message);
        return;
      }
      await loadUsers();
      setSaveMessage('✅ Usuario eliminado de Supabase');
    } else {
      setUsers(prev => prev.filter(u => u.id !== userId));
      setSaveMessage('Usuario eliminado');
    }
    setTimeout(() => setSaveMessage(''), 3000);
  };
  const getRoleBadgeVariant = (role: UserRole) => {
    if (role === 'administrador') return 'success';
    return 'info';
  };
  const columns = [{
    key: 'name',
    header: 'Nombre',
    render: (user: User) => <div className="flex items-center gap-3" data-id="element-2265">
          {user.avatar ? <img src={user.avatar} alt={user.name} className="w-10 h-10 rounded-full object-cover border-2 border-gray-200 dark:border-gray-600" data-id="element-2266" /> : <div className="w-10 h-10 rounded-full bg-sky-100 dark:bg-sky-900 flex items-center justify-center text-sky-600 dark:text-sky-400 font-semibold" data-id="element-2267">
              {user.name.charAt(0)}
            </div>}
          <div data-id="element-2268">
            <div className="font-medium text-gray-900 dark:text-white" data-id="element-2269">
              {user.name}
            </div>
            <div className="text-sm text-gray-500 dark:text-gray-400" data-id="element-2270">
              {user.email}
            </div>
          </div>
        </div>
  }, {
    key: 'role',
    header: 'Rol',
    render: (user: User) => <Badge variant={getRoleBadgeVariant(user.role)} data-id="element-2271">
          {getRoleName(user.role)}
        </Badge>
  }, {
    key: 'createdAt',
    header: 'Fecha de Creación',
    render: (user: User) => <span className="text-sm text-gray-600 dark:text-gray-400" data-id="element-2272">
          {new Date(user.createdAt).toLocaleDateString('es-ES', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })}
        </span>
  }, {
    key: 'actions',
    header: 'Acciones',
    render: (user: User) => <div className="flex items-center gap-2" data-id="element-2273">
          <button onClick={() => handleOpenModal(user)} className="p-2 text-sky-600 hover:bg-sky-50 dark:text-sky-400 dark:hover:bg-sky-900/20 rounded-lg transition-colors" title="Editar usuario" data-id="element-2274">
            <EditIcon className="w-4 h-4" data-id="element-2275" />
          </button>
          <button onClick={() => handleDeleteUser(user.id)} className="p-2 text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20 rounded-lg transition-colors" title="Eliminar usuario" data-id="element-2276">
            <TrashIcon className="w-4 h-4" data-id="element-2277" />
          </button>
        </div>
  }];
  return <div className="space-y-6" data-id="element-2278">
      {/* Header */}
      <div className="flex items-center justify-between" data-id="element-2279">
        <div data-id="element-2280">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2" data-id="element-2281">
            <UserIcon className="w-6 h-6 text-sky-600 dark:text-sky-400" data-id="element-2282" />
            Gestión de Usuarios
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1" data-id="element-2283">
            Administra los usuarios del sistema y asigna roles
            {isConnectedToSupabase && <span className="ml-2 text-green-600 dark:text-green-400" data-id="element-2284">
                • Conectado a Supabase
              </span>}
          </p>
        </div>
        <Button onClick={() => handleOpenModal()} data-id="element-2285">
          <PlusIcon className="w-4 h-4" data-id="element-2286" />
          Nuevo Usuario
        </Button>
      </div>

      {saveMessage && <div className="p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg text-green-600 dark:text-green-400 text-sm" data-id="element-2287">
          {saveMessage}
        </div>}

      {/* Users Table */}
      <Card data-id="element-2288">
        {isLoadingUsers ? <div className="p-8 text-center text-gray-500" data-id="element-2289">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sky-500 mx-auto mb-2" data-id="element-2290"></div>
            Cargando usuarios...
          </div> : <Table columns={columns} data={users} keyExtractor={user => user.id} emptyMessage="No hay usuarios registrados" data-id="element-2291" />}
      </Card>

      {/* User Modal */}
      <Modal isOpen={isModalOpen} onClose={handleCloseModal} title={editingUser ? 'Editar Usuario' : 'Nuevo Usuario'} size="md" icon={<UserIcon className="w-5 h-5" data-id="element-2293" />} accentColor="sky" data-id="element-2292">
        <div className="space-y-6" data-id="element-2294">
          {/* Personal Info Section */}
          <div data-id="element-2295">
            <div className="flex items-center gap-2 mb-4" data-id="element-2296">
              <span className="w-1.5 h-5 rounded-full bg-sky-500" data-id="element-2297"></span>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wide" data-id="element-2298">
                Información Personal
              </h3>
            </div>
            <div className="bg-gray-50 dark:bg-gray-900/30 rounded-xl p-4 space-y-4 border border-gray-100 dark:border-gray-700/50" data-id="element-2299">
              <Input label="Nombre Completo" value={formData.name} onChange={e => setFormData({
              ...formData,
              name: e.target.value
            })} placeholder="Ej: Juan Pérez" data-id="element-2300" />
              <Input label="Correo Electrónico" type="email" value={formData.email} onChange={e => setFormData({
              ...formData,
              email: e.target.value
            })} placeholder="usuario@ejemplo.com" data-id="element-2301" />
            </div>
          </div>

          {/* Access & Security Section */}
          <div data-id="element-2302">
            <div className="flex items-center gap-2 mb-4" data-id="element-2303">
              <span className="w-1.5 h-5 rounded-full bg-amber-500" data-id="element-2304"></span>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wide" data-id="element-2305">
                Acceso y Seguridad
              </h3>
            </div>
            <div className="bg-gray-50 dark:bg-gray-900/30 rounded-xl p-4 space-y-4 border border-gray-100 dark:border-gray-700/50" data-id="element-2306">
              <Input label={editingUser ? 'Nueva Contraseña (opcional)' : 'Contraseña'} type="password" value={formData.password} onChange={e => setFormData({
              ...formData,
              password: e.target.value
            })} placeholder="••••••••" data-id="element-2307" />
              <Select label="Rol del Usuario" options={roleOptions} value={formData.role} onChange={value => setFormData({
              ...formData,
              role: value as UserRole
            })} data-id="element-2308" />
            </div>
          </div>

          {roles.length === 0 && <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3" data-id="element-2309">
              <p className="text-sm text-amber-700 dark:text-amber-300" data-id="element-2310">
                <strong data-id="element-2311">Tip:</strong> Puedes crear más roles desde Configuración
                → Roles para que aparezcan aquí.
              </p>
            </div>}

          {/* Footer */}
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-100 dark:border-gray-700" data-id="element-2312">
            <Button variant="secondary" onClick={handleCloseModal} data-id="element-2313">
              Cancelar
            </Button>
            <Button onClick={handleSaveUser} data-id="element-2314">
              {editingUser ? 'Actualizar' : 'Crear'} Usuario
            </Button>
          </div>
        </div>
      </Modal>
    </div>;
}